import { ReactNode, useEffect, useRef, useState } from 'react';
import { StepperModal } from '@vfit/shared/components';
import { useCmsConfig, CMS_CONFIG } from '@vfit/consumer/data-access';
import { API, getClickToCall, PAGES, useTracking } from '@vfit/shared/data-access';
import { IActionSunrise, IActionType } from '@vfit/shared/models';
import { useQueryClient } from 'react-query';
import { ETrackConvergenceModal, IConvergenceModalProps } from './fallbackModal.models';
import OfferStep from './components/OfferStep/offerStep';
import DetailsStep from './components/DetailsStep/detailsStep';
import {ISupportModule} from "../ProductDetail/productDetail.model";

const FallbackModal = ({
  product,
  isOpen,
  handleClose,
  handleAccept,
  buildTrackingObject,
  offerData,
  taggingPageName,
}: IConvergenceModalProps) => {
  const queryClient = useQueryClient();
  const { trackView, trackWithNewDatalayer } = useTracking({
    event: [`${taggingPageName}`],
    disableInitialTrack: true,
  });
  const stepperRef = useRef<any>(null);
  const { OFFER_STEP, DETAILS_STEP } = ETrackConvergenceModal;
  const [currentStep, setCurrentStep] = useState(OFFER_STEP);
  const supportModulesCms = useCmsConfig(
    CMS_CONFIG[PAGES.CONSUMER],
    API.CMS_GET_SUPPORT_MODULE
  ) as ISupportModule;

  const handleAction = (action: IActionSunrise) => {
    const type = action.type ? +action.type : 0;
    switch (type) {
      case IActionType.CUSTOM_URL:
        handleAccept();
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!action?.title || !action?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: action?.template,
        });
        break;
      default:
        break;
    }
  };

  const trackLink = (label?: string) => {
    trackView({
      event_name: 'ui_interaction',
      event_label_track: `${label}`,
      event_category: 'users',
      event_action: 'click',
      tracking_type: 'link',
    });
  };

  useEffect(() => {
    if (isOpen) {
      const trackingOfferStep = buildTrackingObject(currentStep);
      trackWithNewDatalayer({
        ...trackingOfferStep,
      });
    }
  }, [currentStep, isOpen]);

  const components: ReactNode[] = [
    <OfferStep
      data={{
        title: offerData?.offerProposition?.title || '',
        offerDescription: offerData?.offerProposition?.description || '',
        productTitle: offerData?.offerDetails?.title || '',
        price: offerData?.price || '',
        recurrence: offerData?.recurrence || '',
        priceDescription: offerData?.priceDescription || '',
        productSpecs: offerData?.specs || [],
        firstButtonLabel: offerData?.firstButtonAction?.title || '',
        fistButtonVariant: offerData?.firstButtonAction?.style,
        secondButtonLabel: offerData?.secondButtonAction?.title,
        secondButtonVariant: offerData?.secondButtonAction?.style,
        detailsLabel: offerData?.offerProposition?.showDetailLabel || '',
      }}
      onFirstButtonClick={() => {
        const firstButtonLabel = offerData?.firstButtonAction?.title || '';
        handleAction(offerData?.firstButtonAction);
        trackLink(firstButtonLabel);
      }}
      onSecondButtonClick={() => {
        handleAction(offerData?.secondButtonAction);
        const secondButtonLabel = offerData?.secondButtonAction?.title || '';
        trackLink(secondButtonLabel);
      }}
      onSeeDetailClick={() => {
        stepperRef?.current?._stepperModalGoNext();
        const detailsLabel = offerData?.offerProposition?.showDetailLabel || '';
        const modalContent = document.getElementById('modalContent');
        modalContent?.scrollTo(0, 0);
        trackLink(detailsLabel);
        setCurrentStep(DETAILS_STEP);
      }}
      seconButtonEnabled={offerData?.cTCEnabled}
    />,
    product ? (
      <DetailsStep
        details={{
          title: offerData?.offerDetails?.title || '',
          conditions: offerData?.offerDetails.conditions,
          description: offerData?.offerDetails.description,
          offerDetails: offerData?.offerDetails.offerDetails,
          price: offerData?.price || '',
          recurrence: offerData?.recurrence,
          disableSecondAction:
            offerData?.offerDetails?.disableSecondAction || !offerData?.cTCEnabled,
          firstButtonLabel: offerData?.firstButtonAction?.title || '',
          firstButtonVarian: offerData?.firstButtonAction?.style,
          secondButtonLabel: offerData?.secondButtonAction.title,
          seconButtonVarian: offerData?.secondButtonAction?.style,
        }}
        modalInfo={{
          title: supportModulesCms?.supportmodules?.needHelp?.title || '',
          ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
        }}
        onButtonClick={() => {
          handleAction(offerData?.firstButtonAction);
          const firstButtonLabel = offerData?.firstButtonAction?.title || '';
          trackLink(firstButtonLabel);
        }}
        onSecondButtonClick={() => {
          handleAction(offerData?.secondButtonAction);
          const secondButtonLabel = offerData?.secondButtonAction?.title || '';
          trackLink(secondButtonLabel);
        }}
      />
    ) : (
      <div />
    ),
  ];

  return (
    <StepperModal
      id="fallback-modal"
      isOpen={isOpen}
      handleClose={handleClose}
      ref={stepperRef}
      components={components}
    />
  );
};

export default FallbackModal;
